import { useMemo } from "react"
import useLocalStorage from "hooks/useLocalStorage"

export const DASHBOARD_CONFIG = "dashboardConfig"

export const DASHBOARD_FILTER = "filter"
export const DASHBOARD_COLUMN_VISIBILITY = "columnVisibility"
export const DASHBOARD_COLUMN_WIDTHS = "columnWidths"
export const DASHBOARD_ORDERED_FIELDS = "orderedFields"
export const DASHBOARD_SEARCH = "search"
export const DASHBOARD_SORT = "sort"
export const DASHBOARD_VIEW_SELECTED = "viewSelected"

const DEFAULT_DASHBOARD_CONFIG = {
  [DASHBOARD_FILTER]: { items: [] },
  [DASHBOARD_COLUMN_VISIBILITY]: {},
  [DASHBOARD_COLUMN_WIDTHS]: {},
  [DASHBOARD_ORDERED_FIELDS]: [],
  [DASHBOARD_SEARCH]: "",
  [DASHBOARD_SORT]: [],
  [DASHBOARD_VIEW_SELECTED]: 0,
}

const isRecord = value =>
  value != null && typeof value === "object" && !Array.isArray(value)

const normalizeDashboardConfig = config => ({
  ...DEFAULT_DASHBOARD_CONFIG,
  ...(config ?? {}),
  [DASHBOARD_FILTER]:
    config?.[DASHBOARD_FILTER] ?? DEFAULT_DASHBOARD_CONFIG[DASHBOARD_FILTER],
  [DASHBOARD_COLUMN_VISIBILITY]: isRecord(config?.[DASHBOARD_COLUMN_VISIBILITY])
    ? config[DASHBOARD_COLUMN_VISIBILITY]
    : DEFAULT_DASHBOARD_CONFIG[DASHBOARD_COLUMN_VISIBILITY],
  [DASHBOARD_COLUMN_WIDTHS]: isRecord(config?.[DASHBOARD_COLUMN_WIDTHS])
    ? config[DASHBOARD_COLUMN_WIDTHS]
    : DEFAULT_DASHBOARD_CONFIG[DASHBOARD_COLUMN_WIDTHS],
  [DASHBOARD_ORDERED_FIELDS]: Array.isArray(config?.[DASHBOARD_ORDERED_FIELDS])
    ? config[DASHBOARD_ORDERED_FIELDS]
    : DEFAULT_DASHBOARD_CONFIG[DASHBOARD_ORDERED_FIELDS],
  [DASHBOARD_SEARCH]:
    typeof config?.[DASHBOARD_SEARCH] === "string"
      ? config[DASHBOARD_SEARCH]
      : DEFAULT_DASHBOARD_CONFIG[DASHBOARD_SEARCH],
  [DASHBOARD_SORT]: Array.isArray(config?.[DASHBOARD_SORT])
    ? config[DASHBOARD_SORT]
    : DEFAULT_DASHBOARD_CONFIG[DASHBOARD_SORT],
  [DASHBOARD_VIEW_SELECTED]:
    typeof config?.[DASHBOARD_VIEW_SELECTED] === "number"
      ? config[DASHBOARD_VIEW_SELECTED]
      : DEFAULT_DASHBOARD_CONFIG[DASHBOARD_VIEW_SELECTED],
})

/**
 * Custom hook for managing dashboard configuration using local storage.
 *
 * @constant {string} DASHBOARD_CONFIG - The key used to store dashboard configuration in local storage.
 * @constant {string} DASHBOARD_FILTER - The key for the filter configuration.
 * @constant {string} DASHBOARD_COLUMN_VISIBILITY - The key for the column visibility settings.
 * @constant {string} DASHBOARD_COLUMN_WIDTHS - The key for the column width settings.
 * @constant {string} DASHBOARD_ORDERED_FIELDS - The key for the ordered fields configuration.
 * @constant {string} DASHBOARD_SEARCH - The key for the search query.
 * @constant {string} DASHBOARD_SORT - The key for the sorting configuration.
 *
 * @returns {[Object, Function]} Returns an array containing the dashboard configuration and a function to update it.
 *
 * @property {Object} dashboardConfig - The current configuration of the dashboard.
 * @property {Object} dashboardConfig.filter - The filter settings, containing an array of items.
 * @property {Object} dashboardConfig.columnVisibility - The visibility configuration for dashboard columns.
 * @property {Object} dashboardConfig.columnWidths - The persisted width of each dashboard column.
 * @property {string[]} dashboardConfig.orderedFields - The ordered fields of the dashboard.
 * @property {string} dashboardConfig.search - The current search query.
 * @property {Array} dashboardConfig.sort - The current sorting configuration.
 *
 * @property {Function} updateConfig - A function to update specific parts of the dashboard configuration.
 * @param {string} key - The key of the configuration property to update.
 * @param {any} value - The new value to set for the specified configuration key.
 */
const useDashboardConfig = () => {
  const [storedDashboardConfig, setDashboardConfig] = useLocalStorage(
    DASHBOARD_CONFIG,
    DEFAULT_DASHBOARD_CONFIG,
  )

  const dashboardConfig = useMemo(
    () => normalizeDashboardConfig(storedDashboardConfig),
    [storedDashboardConfig],
  )

  const updateConfig = (key, value) => {
    setDashboardConfig(prevConfig => {
      return normalizeDashboardConfig({
        ...prevConfig,
        [key]: value,
      })
    })
  }

  return [dashboardConfig, updateConfig]
}

export default useDashboardConfig
